/**
 * Design System — Theme → CSS custom-property emitter.
 *
 * Produces the flat `--color-*` / `--space-*` / `--radius-*` / `--fs-*` / … map
 * that the ThemeProvider paints on `:root`. Emits the SAME `--color-*` names the
 * app shell + colors.css already reference, so mounting the provider unifies the
 * whole app onto the token layer with no per-component renaming.
 */
import type { Theme, ThemeMode, TenantThemeOverride } from './theme.js';
import type { SemanticColors } from './tokens/colors.js';
import { chartSeriesLight, chartSeriesDark } from './tokens/chartSeries.js';

const px = (n: number): string => `${n}px`;

/** Merge a tenant's per-mode color overrides onto the base semantic colors. */
function resolveColors(theme: Theme, mode: ThemeMode, override?: TenantThemeOverride): SemanticColors {
  const base = theme.colors[mode];
  const ov = override?.colors?.[mode];
  return ov ? { ...base, ...ov } : base;
}

const COLOR_VAR: Readonly<Record<keyof SemanticColors, string>> = {
  brandPrimary: '--color-brand-primary',
  brandPrimaryHover: '--color-brand-primary-hover',
  brandSecondary: '--color-brand-secondary',
  bgCanvas: '--color-bg-canvas',
  bgSurface: '--color-bg-surface',
  bgElevated: '--color-bg-elevated',
  bgMuted: '--color-bg-muted',
  bgHover: '--color-bg-hover',
  bgSelected: '--color-bg-selected',
  bgActiveMenu: '--color-bg-active-menu',
  bgOverlay: '--color-bg-overlay',
  textPrimary: '--color-text-primary',
  textSecondary: '--color-text-secondary',
  textMuted: '--color-text-muted',
  textInverse: '--color-text-inverse',
  textLink: '--color-text-link',
  borderDefault: '--color-border-default',
  borderStrong: '--color-border-strong',
  borderFocus: '--color-border-focus',
  success: '--color-success',
  successBg: '--color-success-bg',
  warning: '--color-warning',
  warningBg: '--color-warning-bg',
  danger: '--color-danger',
  dangerBg: '--color-danger-bg',
  info: '--color-info',
  infoBg: '--color-info-bg',
};

/** Build the full CSS-variable map for a theme + mode (+ optional tenant override). */
export function buildThemeVars(
  theme: Theme,
  mode: ThemeMode,
  override?: TenantThemeOverride,
): Record<string, string> {
  const vars: Record<string, string> = {};
  const colors = resolveColors(theme, mode, override);

  for (const key of Object.keys(COLOR_VAR) as (keyof SemanticColors)[]) {
    vars[COLOR_VAR[key]] = colors[key];
  }
  vars['--focus-ring'] = '0 0 0 2px var(--color-bg-canvas), 0 0 0 4px var(--color-border-focus)';

  // categorical chart-series palette (theme-aware, color-blind-safe) — MF-34
  const chartSeries = mode === 'dark' ? chartSeriesDark : chartSeriesLight;
  chartSeries.forEach((hex, i) => {
    vars[`--color-chart-${i + 1}`] = hex;
  });

  // typography
  const fam = override?.fontFamilySans ?? theme.typography.fontFamily.sans;
  vars['--font-sans'] = fam;
  vars['--font-mono'] = theme.typography.fontFamily.mono;
  for (const [k, v] of Object.entries(theme.typography.fontSize)) vars[`--fs-${k}`] = px(v);
  for (const [k, v] of Object.entries(theme.typography.fontWeight)) vars[`--fw-${k}`] = String(v);
  for (const [k, v] of Object.entries(theme.typography.lineHeight)) vars[`--lh-${k}`] = String(v);
  for (const [k, v] of Object.entries(theme.typography.letterSpacing)) vars[`--ls-${k}`] = v;

  // spacing / radius / shadow
  for (const [k, v] of Object.entries(theme.spacing)) vars[`--space-${k}`] = px(v);
  for (const [k, v] of Object.entries(theme.radius)) vars[`--radius-${k}`] = px(v);
  for (const [k, v] of Object.entries(theme.borderWidth)) vars[`--border-${k}`] = px(v);
  for (const [k, v] of Object.entries(theme.shadow)) vars[`--shadow-${k}`] = v;

  // motion
  for (const [k, v] of Object.entries(theme.animation.duration)) vars[`--duration-${k}`] = v;
  for (const [k, v] of Object.entries(theme.animation.easing)) vars[`--ease-${k}`] = v;

  // z-index
  for (const [k, v] of Object.entries(theme.zIndex)) vars[`--z-${k}`] = String(v);

  return vars;
}

/** Serialize a variable map to a CSS block under the given selector. */
export function themeVarsToCss(vars: Record<string, string>, selector = ':root'): string {
  const body = Object.entries(vars)
    .map(([k, v]) => `  ${k}: ${v};`)
    .join('\n');
  return `${selector} {\n${body}\n}`;
}
